import { watch } from "vue";

/**
 * Run `callback` after the overlay containing the list has committed its open
 * layout. The list gets its box as its popup/bottom-sheet opens, so the
 * first animation frame still precedes the top-layer popover's Floating-UI
 * positioning (and the sheet's height): the scroll container would measure 0 and
 * scrollIntoView would no-op. The second frame runs after that layout is painted.
 *
 * @param {() => void} callback
 */
function runAfterOverlayLayout(callback) {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

/**
 * Reveal the active row each time the list's dropdown/menu opens (opt-in). The list stays mounted
 * while its popup is closed, so an opening is `shown` turning true. The
 * scroll is deferred until the containing overlay has laid out; `block: "nearest"`
 * no-ops when the row is already visible, so it only scrolls a selection that
 * sits below the fold (e.g. inside an auto-expanded accordion branch).
 *
 * @param {import('vue').Ref<HTMLElement | null>} listRef - the scroll container ref.
 * @param {() => boolean} enabled - getter for the opt-in flag.
 * @param {import('vue').Ref<boolean>} shown - whether the list is rendered with a box.
 */
export function useScrollSelectedIntoView(listRef, enabled, shown) {
  /** Scroll the active row into view, if there is one. */
  function scrollSelectedRowIntoView() {
    const active = listRef.value?.querySelector("sp-list-item[active]");
    active?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "auto" });
  }

  /** Once the list is shown, reveal the selected row when the opt-in flag is set. */
  function scrollSelectedRowIntoViewOnOpen(isShown) {
    if (!isShown || !enabled()) {
      return;
    }
    runAfterOverlayLayout(scrollSelectedRowIntoView);
  }

  watch(shown, scrollSelectedRowIntoViewOnOpen);
}
